import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, DollarSign, TrendingUp, BarChart3, PieChart, Users, FileText, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { api } from '@/services/api';
import { LogoFull } from '@/components/Logo';

const COLORS = ['#1A3A64', '#00AEEF', '#F59E0B', '#E74C3C', '#3D5A80', '#8DA0B9'];

const StatCard = ({ icon: Icon, label, value, sub, color = '#1A3A64' }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl border border-[#DCE4ED] p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <span className="text-sm text-[#8DA0B9]">{label}</span>
    </div>
    <p className="text-2xl font-semibold text-[#0D1B2A]">{value}</p>
    {sub && <p className="text-xs text-[#8DA0B9] mt-1">{sub}</p>}
  </motion.div>
);

const AdvancedAnalytics = () => {
  const [revenue, setRevenue] = useState(null);
  const [claims, setClaims] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('revenue');

  useEffect(() => {
    const load = async () => {
      try {
        const [r, c, cv] = await Promise.all([
          api.getRevenueAnalytics(),
          api.getClaimsAnalytics(),
          api.getConversionAnalytics()
        ]);
        setRevenue(r);
        setClaims(c);
        setConversion(cv);
      } catch (err) {
        console.error('Analytics load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  const tabs = [
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'conversion', label: 'Conversion', icon: TrendingUp },
    { id: 'claims', label: 'Claims', icon: AlertCircle },
  ];

  const industryData = conversion?.by_industry?.map(i => ({ name: i.industry, policies: i.count, premium: i.monthly_premium })) || [];
  const claimStatusData = claims ? Object.entries(claims.by_status || {}).map(([k, v]) => ({ name: k, count: v.count, amount: v.amount })) : [];

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="advanced-analytics-page">
      <header className="bg-white border-b border-[#DCE4ED]">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors">
              <ArrowLeft size={18} /><span className="text-sm">Admin</span>
            </Link>
            <div className="flex items-center gap-2">
              <LogoFull size="small" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-[#0D1B2A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <BarChart3 size={24} className="inline mr-2 text-[#1A3A64]" />Advanced Analytics
          </h1>
          <div className="flex gap-1 bg-[#F0F4F8] p-1 rounded-lg w-fit">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-white text-[#0D1B2A] shadow-sm' : 'text-[#8DA0B9] hover:text-[#3D5A80]'}`} data-testid={`tab-${t.id}`}>
                <t.icon size={16} />{t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Revenue Tab */}
        {tab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={DollarSign} label="Total Revenue" value={`$${(revenue?.total_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`} sub={`${revenue?.total_transactions || 0} transactions`} />
              <StatCard icon={TrendingUp} label="Avg Transaction" value={`$${(revenue?.avg_transaction || 0).toFixed(2)}`} color="#00AEEF" />
              <StatCard icon={BarChart3} label="Pending Payments" value={revenue?.pending_payments || 0} color="#F59E0B" />
              <StatCard icon={FileText} label="Monthly Revenue" value={revenue?.by_billing_cycle?.monthly ? `$${revenue.by_billing_cycle.monthly.total.toFixed(2)}` : '$0'} sub="Monthly subscriptions" color="#1A3A64" />
            </div>
            <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
              <h3 className="font-medium text-[#0D1B2A] mb-4">Revenue Trend</h3>
              {revenue?.revenue_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenue.revenue_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#DCE4ED" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#8DA0B9' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#8DA0B9' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="revenue" stroke="#1A3A64" strokeWidth={2} dot={{ fill: '#1A3A64' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[#8DA0B9]">No revenue data yet. Payments will appear here.</div>
              )}
            </div>
          </div>
        )}

        {/* Conversion Tab */}
        {tab === 'conversion' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={FileText} label="Total Quotes" value={conversion?.total_quotes || 0} />
              <StatCard icon={FileText} label="Total Policies" value={conversion?.total_policies || 0} color="#00AEEF" />
              <StatCard icon={TrendingUp} label="Quote → Policy" value={`${conversion?.quote_to_policy_rate || 0}%`} color="#F59E0B" />
              <StatCard icon={DollarSign} label="Policy → Paid" value={`${conversion?.policy_to_paid_rate || 0}%`} color="#1A3A64" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <h3 className="font-medium text-[#0D1B2A] mb-4">Policy Funnel</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Quotes', value: conversion?.total_quotes || 0, pct: 100 },
                    { label: 'Policies Created', value: conversion?.total_policies || 0, pct: conversion?.total_quotes ? Math.round((conversion.total_policies / conversion.total_quotes) * 100) : 0 },
                    { label: 'Active/Paid', value: conversion?.paid_policies || 0, pct: conversion?.total_policies ? Math.round((conversion.paid_policies / conversion.total_policies) * 100) : 0 },
                  ].map((step, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#3D5A80]">{step.label}</span>
                        <span className="font-medium text-[#0D1B2A]">{step.value} ({step.pct}%)</span>
                      </div>
                      <div className="h-2 bg-[#E8EEF4] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${step.pct}%`, backgroundColor: COLORS[i] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <h3 className="font-medium text-[#0D1B2A] mb-4">By Industry</h3>
                {industryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={industryData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DCE4ED" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8DA0B9' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#8DA0B9' }} />
                      <Tooltip />
                      <Bar dataKey="policies" fill="#1A3A64" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-[#8DA0B9]">No industry data yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Claims Tab */}
        {tab === 'claims' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <StatCard icon={AlertCircle} label="Total Claims" value={claims?.total_claims || 0} />
              <StatCard icon={DollarSign} label="Total Claimed" value={`$${(claims?.total_claimed_amount || 0).toLocaleString()}`} color="#E74C3C" />
              <StatCard icon={Users} label="Open Claims" value={claims?.by_status?.open?.count || 0} color="#F59E0B" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <h3 className="font-medium text-[#0D1B2A] mb-4">Claims by Status</h3>
                {claimStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RPieChart>
                      <Pie data={claimStatusData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, count }) => `${name}: ${count}`}>
                        {claimStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </RPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-[#8DA0B9]">No claims data yet.</div>
                )}
              </div>
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <h3 className="font-medium text-[#0D1B2A] mb-4">Recent Claims</h3>
                {claims?.recent_claims?.length > 0 ? (
                  <div className="space-y-3">
                    {claims.recent_claims.map(c => (
                      <div key={c.claim_id} className="flex items-center justify-between p-3 rounded-lg bg-[#F0F4F8]">
                        <div>
                          <p className="text-sm font-medium text-[#0D1B2A]">{c.title || c.claim_id}</p>
                          <p className="text-xs text-[#8DA0B9]">{new Date(c.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'open' ? 'bg-amber-100 text-amber-700' : c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : c.status === 'denied' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>{c.status}</span>
                          <p className="text-xs text-[#8DA0B9] mt-1">${(c.estimated_amount || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-[#8DA0B9]">No claims yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
